
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../api';
import appConfig from '../config/appConfig';
import { setAuth, isAuthenticated } from '../utils/auth';
import AdminBg from '../assets/img/admingall/loginbg.png';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';

const OTP_EXPIRY_SECONDS = 300;

const Login = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [otp, setOtp] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [timer, setTimer] = useState(OTP_EXPIRY_SECONDS);
  const [otpExpired, setOtpExpired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/adminhome', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (step !== 'verifyOtp' || timer <= 0) return undefined;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer, step]);

  useEffect(() => {
    if (timer === 0) {
      setOtpExpired(true);
    }
  }, [timer]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/login`, { username, password });
      setAuth(response.data.token, response.data.user);
      navigate(response.data.redirect || '/adminhome', { replace: true });
    } catch (loginError) {
      setError(loginError.response?.data?.message || 'Server error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestOTP = async () => {
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/login/request-otp`);
      const expirySeconds = Math.floor((response.data.expiresInMs || OTP_EXPIRY_SECONDS * 1000) / 1000);
      setMessage(response.data.message);
      setStep('verifyOtp');
      setTimer(expirySeconds);
      setOtpExpired(false);
    } catch (otpError) {
      setError(otpError.response?.data?.message || 'Server error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpExpired) {
      setError('OTP has expired. Please request a new OTP.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/login/verify-update`, {
        otp,
        newUsername,
        newPassword,
      });
      setMessage(response.data.message);
      setStep('login');
      setOtp('');
      setNewUsername('');
      setNewPassword('');
    } catch (verifyError) {
      setError(verifyError.response?.data?.message || 'Server error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
  const toggleNewPasswordVisibility = () => setNewPasswordVisible(!newPasswordVisible);

  return (
    <div
      className="relative h-screen w-full flex items-center justify-center bg-cover bg-center"
      style={{ backgroundImage: `url(${AdminBg})`, filter: 'brightness(0.7)' }}
    >
      {step === 'login' && (
        <div className="w-full max-w-lg mx-auto bg-gradient-to-b from-black to-gray-900 rounded-lg shadow-xl overflow-hidden">
          <form onSubmit={handleLoginSubmit} className="p-6">
            <h1 className="text-3xl font-bold text-center text-blue-500 mb-2">Admin Login</h1>
            <p className="text-center text-gray-400 text-sm mb-6">{appConfig.hotel.name}</p>
            <div className="mb-4 flex items-center bg-gray-800 p-3 rounded-lg">
              <FaEnvelope className="text-blue-500 mr-2" />
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-transparent text-white outline-none"
                required
              />
            </div>
            <div className="mb-4 flex items-center bg-gray-800 p-3 rounded-lg relative">
              <FaLock className="text-blue-500 mr-2" />
              <input
                type={passwordVisible ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent text-white outline-none"
                required
              />
              <button type="button" onClick={togglePasswordVisibility} className="absolute right-3 text-blue-500">
                {passwordVisible ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {error && <p className="text-red-500 text-center mb-4">{error}</p>}
            {message && <p className="text-green-500 text-center mb-4">{message}</p>}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2 rounded-lg"
            >
              {isSubmitting ? 'Signing in...' : 'Login'}
            </button>
            <button
              type="button"
              onClick={() => {
                setError('');
                setMessage('');
                setStep('otpRequest');
              }}
              className="w-full text-blue-500 mt-4 hover:underline text-center"
            >
              Forgot Username/Password?
            </button>
          </form>
        </div>
      )}

      {step === 'otpRequest' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-gray-900 p-6 rounded-lg w-96">
            <h2 className="text-2xl font-bold text-white mb-2 text-center">Reset Credentials</h2>
            <p className="text-gray-400 text-sm text-center mb-4">
              An OTP will be sent to the registered admin email.
            </p>
            {error && <p className="text-red-500 text-center mb-4">{error}</p>}
            <button
              onClick={handleRequestOTP}
              disabled={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2 rounded-lg"
            >
              {isSubmitting ? 'Sending...' : 'Send OTP'}
            </button>
            <button
              type="button"
              onClick={() => setStep('login')}
              className="w-full text-red-500 mt-2 hover:underline text-center"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'verifyOtp' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-gray-900 p-6 rounded-lg w-96">
            <h2 className="text-2xl font-bold text-white mb-4 text-center">Verify OTP & Update</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleVerifyOtp();
              }}
            >
              <input
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full mb-4 p-3 bg-gray-700 text-white border border-gray-500 rounded"
                required
              />
              <input
                type="text"
                placeholder="New Username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                className="w-full mb-4 p-3 bg-gray-700 text-white border border-gray-500 rounded"
                required
              />
              <div className="relative">
                <input
                  type={newPasswordVisible ? 'text' : 'password'}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full mb-4 p-3 bg-gray-700 text-white border border-gray-500 rounded"
                  required
                />
                <button
                  type="button"
                  onClick={toggleNewPasswordVisibility}
                  className="absolute right-3 top-3 text-blue-500"
                >
                  {newPasswordVisible ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {otpExpired && <p className="text-red-500 text-center mb-4">OTP has expired. Please request a new OTP.</p>}
              <div className="text-center text-white mb-4">
                {timer > 0 && <p>OTP expires in {timer} seconds</p>}
              </div>
              {error && <p className="text-red-500 text-center mb-4">{error}</p>}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2 rounded-lg"
              >
                {isSubmitting ? 'Updating...' : 'Verify & Update'}
              </button>
              <button
                type="button"
                onClick={handleRequestOTP}
                disabled={isSubmitting}
                className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:opacity-60 text-white mt-2 py-2 rounded-lg"
              >
                Resend OTP
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
